import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import UploadService, {
  IAfterFileUpload,
} from "services/uploadService/uploadService";
import * as FirebaseStoragePaths from "services/firebaseService/storagePaths";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { getBase64 } from "helpers/profileHelper";
import logger from "logging/logger";
import { useI18n } from "i18n";
import UploadCaption, {
  BestUploadSizes,
  MaxUploadSizes,
} from "modules/uploader/UploadCaption";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useMediaQuery } from "@remo-co/ui-core/src/hooks/useMediaQuery";
import { useTheme } from "@remo-co/ui-core/src/hooks/useTheme";
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_SIZE } from "./constants";
import defaultProfile from "../userAvatar/assets/user.svg";
import ImageEditor from "./ImageEditor";
import { useStyles } from "./styles";

interface Props {
  size: number;
  profileImage: string;
  onLoading?: (isLoading: boolean) => void;
  afterImageUpload: IAfterFileUpload<unknown>;
  setProfileImage: (image: string) => void;
  noImageLabel?: string;
  withImageLabel?: string;
  hideRemoveBtn?: boolean;
  showTitle?: boolean;
  onClear?: () => void;
  isLoading: boolean;
}
// TODO: add translation
const ProfileImageEditor = ({
  size,
  profileImage,
  onLoading,
  afterImageUpload,
  setProfileImage,
  noImageLabel = "eventForm:add.profile.picture",
  withImageLabel = "eventForm:change.profile.picture",
  hideRemoveBtn,
  showTitle,
  onClear = () => {},
  isLoading,
}: Props): JSX.Element | null => {
  const { addErrorNotification } = useNotificationActions();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const videoImageUploaderRef = useRef<UploadService<unknown>>();
  const [image, setImage] = useState("");
  const [isOpenEditor, setIsOpenEditor] = useState<boolean>(false);
  const openEditor = () => setIsOpenEditor(true);
  const closeEditor = () => setIsOpenEditor(false);
  const isDefault = image === "default" || !image;
  const currentImage = isDefault ? defaultProfile : image;
  const { t } = useI18n(["common", "eventForm"]);
  const classes = useStyles();

  const clearImage = () => {
    setImage("default");
    setProfileImage("default");
    onClear();
  };

  const handleCancel = useCallback(() => {
    setImage(profileImage);
    setProfileImage(profileImage);
    closeEditor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileImage]);

  const { getRootProps, getInputProps, acceptedFiles, fileRejections } =
    useDropzone({
      accept: ACCEPTED_IMAGE_TYPES,
      maxSize: MAX_IMAGE_SIZE,
      multiple: false,
    });
  const { onClick: onDropzoneClick, ...dropzoneProps } = getRootProps();

  const uploadFile = (file: Blob) => {
    if (onLoading) {
      onLoading(true);
    }
    videoImageUploaderRef.current?.addFile(file, {
      id: "image",
      name: "picture",
      type: "images",
    });
  };
  const afterUpload: IAfterFileUpload<unknown> = (
    err,
    origin,
    url,
    file,
    path,
    // eslint-disable-next-line max-params
  ) => {
    if (!url) {
      return;
    }

    logger.info(`[ProfileImageEditor] afterUpload ${url}`);
    setImage(url);

    if (afterImageUpload) {
      afterImageUpload(err, origin, url, file, path);
    }
    closeEditor();
  };

  useEffect(() => {
    setImage(profileImage);
  }, [profileImage]);

  useEffect(() => {
    if (fileRejections?.length > 0) {
      addErrorNotification({
        message: t("eventForm:img.format.info"),
        autoDismiss: 5,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileRejections]);

  useEffect(() => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];

      if (file.type === "image/gif") {
        uploadFile(acceptedFiles[0]);
        return;
      }

      getBase64(file)
        // eslint-disable-next-line promise/always-return, promise/prefer-await-to-then
        .then((url) => {
          setImage(url);
          openEditor();
        })
        // eslint-disable-next-line promise/prefer-await-to-then
        .catch(() =>
          addErrorNotification({
            message: t("eventForm:upload.image.error"),
          }),
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedFiles]);

  useEffect(() => {
    videoImageUploaderRef.current = new UploadService(
      "video-image-uploader-picture",
      {
        afterFileUpload: afterUpload,
        path: FirebaseStoragePaths.PROFILE_IMAGE,
        level: FirebaseStoragePaths.UPLOAD_LEVEL.USER,
        maxFileSize: "512KB",
        acceptedFileTypes: ACCEPTED_IMAGE_TYPES,
        fileValidateTypeLabelExpectedTypes: t("eventForm:img.format.info"),
      },
    );

    return () => {
      if (videoImageUploaderRef.current) {
        videoImageUploaderRef.current.destroy();
        videoImageUploaderRef.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return null;

  return (
    <div className="profile-image-editor-ctn">
      <div className="file-uploader-container">
        <input
          id="video-image-uploader-picture"
          data-testid="image-upload-input"
          type="file"
          name="filepond"
          style={{ visibility: "hidden" }}
        />
      </div>
      {isOpenEditor && (
        <ImageEditor
          image={currentImage}
          onSave={uploadFile}
          onClose={handleCancel}
        />
      )}
      {!isOpenEditor && (
        <Container className={classes.imageContainer}>
          <div className={classes.avatar} style={{ width: size, height: size }}>
            <img src={currentImage} alt={currentImage} />
          </div>
          {isDefault && (
            <Container className={classes.controlsContainer}>
              {showTitle && (
                <Typography variant="h3" color="inherit">
                  {t("eventForm:edit.profile.title")}
                </Typography>
              )}
              <input {...getInputProps()} />
              <Button
                {...getRootProps()}
                autoFocus
                data-testid="no-image-button"
                variant="secondary"
                color="blue"
                size={isSmallScreen ? "sm" : undefined}
              >
                {t(noImageLabel)}
              </Button>
              <UploadCaption
                bestSize={BestUploadSizes.small}
                maxSize={MaxUploadSizes.small}
                allowGif
              />
            </Container>
          )}
          {!isDefault && (
            <Container className={classes.controlsContainer} {...dropzoneProps}>
              {showTitle && (
                <Typography variant="h3" color="inherit">
                  {t("eventForm:edit.profile.title")}
                </Typography>
              )}
              <input {...getInputProps()} />
              <Container
                flex
                column={{ xs: true, sm: true, md: false }}
                justifyContent={{ sm: "center", md: "flex-start" }}
              >
                <Button
                  autoFocus
                  data-testid="with-image-button"
                  onClick={onDropzoneClick}
                  variant="secondary"
                  color="blue"
                  size={isSmallScreen ? "sm" : undefined}
                >
                  {t(withImageLabel)}
                </Button>
                {!hideRemoveBtn && (
                  <Button
                    variant="warning"
                    color="red"
                    id="my-profile_text-link-cta-remove"
                    onClick={clearImage}
                    size={isSmallScreen ? "sm" : undefined}
                  >
                    {t("remove")}
                  </Button>
                )}
              </Container>
              <UploadCaption
                bestSize={BestUploadSizes.small}
                maxSize={MaxUploadSizes.small}
                allowGif
              />
            </Container>
          )}
        </Container>
      )}
    </div>
  );
};

export default ProfileImageEditor;
